import { Box, Button, Flex, HTMLChakraProps, Icon, Text } from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import LinkIcon from '@/assets/icons/ic-open-in-new.svg';
import PdfIcon from '@/assets/icons/ic-pdf.svg';

import { useAnalytics } from '@/context/Analytics';
import { MaterialType } from '@/enums/MaterialType';
import { useQueryParams } from '@/hooks/useQueryParams';
import { OriginalsMaterial } from '@/resources/OriginalsMaterial';
import { SlugIdParam } from '@/utils/url-query-string';

type IconType = React.FunctionComponent<React.SVGAttributes<SVGElement>>;

const icons: Record<MaterialType, IconType> = {
  file: PdfIcon,
  external_link: LinkIcon,
};

interface IMaterialsCardProps extends HTMLChakraProps<'div'> {
  material: OriginalsMaterial;
}

export const MaterialsCard: FC<IMaterialsCardProps> = ({ material, ...props }) => {
  const analytics = useAnalytics();
  const { t } = useTranslation();
  const { id: seriesId } = useQueryParams<{ id: string }>({
    id: SlugIdParam,
  });

  const RenderIcon = icons[material.materialType];
  const isFile = material.materialType === MaterialType.File;
  const isExternalLink = material.materialType === MaterialType.ExternalLink;

  return (
    <Flex
      flexDir="column"
      justifyContent="space-between"
      backgroundColor="white"
      w={{ base: '240px', md: '288px' }}
      minH="264px"
      boxShadow="0px 8px 16px rgba(0, 0, 0, 0.1)"
      borderRadius={2}
      p="24px"
      {...props}
    >
      <Box>
        <Icon as={RenderIcon} w="18px" h="18px" />
        <Text mt="5px" fontWeight="bold" fontSize="21px">
          {material.name}
        </Text>
      </Box>
      <Text>
        {isFile && t('episodeMaterial.file')}
        {isExternalLink && t('episodeMaterial.externalLink')}
      </Text>
      <Button
        as="a"
        target="_blank"
        rel="noopener noreferrer"
        href={material.fileUrl || material.fileLink}
        color="white"
        alignSelf="flex-end"
        fontWeight="bold"
        fontSize="16px"
        onClick={() =>
          analytics.track(isFile ? 'originals_materials_download' : 'originals_materials_link', {
            originals_id: seriesId,
            url: material.fileUrl || material.fileLink,
          })
        }
      >
        {isFile && t('common.download')}
        {isExternalLink && t('common.openLink')}
      </Button>
    </Flex>
  );
};
